import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { usersApi } from "../services/users";
import type { PayoutBatch } from "../types/payouts";
import { User } from "../types/User";
import Loader from "../components/Loader";
import PayoutDetailsModal from "../components/PayoutDetailsModal";
import { referralBonusOf } from "../components/ReferralCashout";
import ChargebackUploadModal from "../components/ChargebackUploadModal";
import { PayoutRowProgress, PayoutTrackerSheet } from "../components/PayoutTracker";
import { isMoving } from "../utils/payoutTracker";
import "./PayoutHistoryPage.css";
import { formatMoneyByCurrency, moneyByCurrencyFrom } from "../utils/currency";
import { absMediaUrl } from "../utils/mediaUrl";
import { payoutDestinationView, type DestinationIconKind } from "../utils/payoutDestination";
import { t, formatLocale, type MessageKey } from "../i18n";

// "Aug 21". The table never prints a year in the row (in Portuguese a dated
// "3 de dez. de 2025" alone outgrows a small phone): a year row heads the
// payouts from before this year instead. The payout sheet's title carries it.
function fmtDate(iso: string, withYear = false) {
  return new Date(iso).toLocaleDateString(formatLocale(), withYear ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" });
}
const yearOf = (iso: string) => new Date(iso).getFullYear();

type PayoutRow = PayoutBatch;

// Every payout provider's status reaches us mapped onto these five (server
// models/Payout.js payoutStatus). Anything else — awaiting_funds, preparing,
// a status a newer server adds — reads as pending until it settles.
type StatusKind = "paid" | "pending" | "in_transit" | "failed" | "canceled";
function statusKind(s?: string): StatusKind {
  const x = (s || "pending").toLowerCase().replace(/[\s-]/g, "_");
  if (x === "paid") return "paid";
  if (x === "in_transit") return "in_transit";
  if (x === "failed") return "failed";
  if (x === "canceled" || x === "cancelled") return "canceled";
  return "pending";
}
const STATUS_LABEL: Record<StatusKind, MessageKey> = {
  paid: "payoutHistoryPage.paid",
  pending: "payoutHistoryPage.pending",
  in_transit: "payoutHistoryPage.inTransit",
  failed: "payoutHistoryPage.failed",
  canceled: "payoutHistoryPage.canceled",
};

// Where the money went — a bank, a debit card (Instant Payouts) or a wallet
// (MassPay); a kind this build doesn't know gets a plain arrow. Drawn, never
// an emoji.
const DestinationIcon: React.FC<{ kind: DestinationIconKind }> = ({ kind }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {kind === "bank" ? (
      <><path d="M3 10 12 4l9 6" /><path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" /><path d="M3 20h18" /></>
    ) : kind === "card" ? (
      <><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 10h19" /></>
    ) : kind === "wallet" ? (
      <><path d="M19 7V5.5A1.5 1.5 0 0 0 17.5 4h-12A2.5 2.5 0 0 0 3 6.5v11A2.5 2.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" /><path d="M21 9h-5a3 3 0 0 0 0 6h5Z" /></>
    ) : (
      <><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></>
    )}
  </svg>
);

// The Balance tab's green chevron: the row opens the payout's requests.
const Chevron = () => (
  <svg className="payout-row-chevron" width="9" height="14" viewBox="0 0 9 14" fill="none" stroke="#24C961" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m1.5 1.5 5.5 5.5-5.5 5.5" />
  </svg>
);

const PayoutHistoryPage: React.FC = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profile, setProfile] = useState<User | null>(null);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [payouts, setPayouts] = useState<PayoutRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openPayout, setOpenPayout] = useState<{ batchId: string; label?: string } | null>(null);
  // Payout tracker (1.5.2): a moving payout's row opens its tracker first.
  const [tracking, setTracking] = useState<PayoutRow | null>(null);
  // Chargeback upload modal: the batch the talent is uploading evidence for.
  // Clicking the "Action Needed" badge on a row opens this directly — no
  // intermediate popover, the badge itself IS the call to action.
  const [chargebackUploadFor, setChargebackUploadFor] = useState<{
    batchId: string;
    pendingCount: number;
  } | null>(null);

  // Every entry point (Profile "View All", cashout popup) navigates here from
  // a scrolled-down page and React Router keeps scrollY — without this reset
  // the page top (incl. the back arrow) starts hidden under the sticky header.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const profRes = await usersApi.get(user.slug);
        if (cancelled) return;
        setProfile(profRes);
      } catch {
        if (!cancelled) setError(t("payoutHistoryPage.couldNotLoadProfile"));
      }
      try {
        const payRes = await usersApi.payouts(user.slug);
        if (cancelled) return;
        setPayouts(Array.isArray(payRes) ? payRes : []);
      } catch {
        if (!cancelled) {
          setError(
            (prev) =>
              [prev, "Could not load payout history."].filter(Boolean).join(" ")
          );
          setPayouts([]);
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isAuthenticated]);

  const displayName = profile?.name || user?.name || t("payoutHistoryPage.creator");

  if (!user) {
    return null;
  }

  const money = (p: PayoutRow) =>
    p.totalsByCurrency && Object.keys(p.totalsByCurrency).length > 0
      ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "net"))
      : `$${p.totalNet.toFixed(2)}`;

  // One status per payout: the evidence call to action, else the dispute
  // count, else the payout's own status.
  const status = (p: PayoutRow) => {
    // Phase 2.5: needs evidence when at least one disputed order in this
    // payout is missing track-history. The orange "Action Needed" badge
    // opens the chargeback upload modal directly.
    const needsEvidence = p.disputedOrdersNeedingTrackHistory || 0;
    const disputes = p.disputedOrdersCount || 0;
    if (needsEvidence > 0) {
      return (
        <button
          type="button"
          className="payout-status-badge payout-status-badge--action-needed"
          aria-label={t("payoutHistoryPage.actionNeededUploadTracklists", { count: needsEvidence })}
          onClick={(e) => {
            e.stopPropagation();
            setChargebackUploadFor({ batchId: p.batchId, pendingCount: needsEvidence });
          }}
        >
          <span>{t("payoutHistoryPage.actionNeeded")}</span>
        </button>
      );
    }
    if (disputes > 0) {
      return (
        <span className="payout-status-badge payout-status-badge--disputed" title={t("payoutHistoryPage.disputesFlaggedOnThis")}>
          {disputes} {disputes === 1 ? t("payoutHistoryPage.dispute") : t("payoutHistoryPage.disputes")}
        </span>
      );
    }
    // Still on its way: when it lands, not a bare PENDING (payout tracker, 1.5.2).
    if (isMoving(p)) return <PayoutRowProgress payout={p} />;
    const k = statusKind(p.payoutStatus);
    return <span className={`payout-status-badge payout-status-badge--${k}`}>{t(STATUS_LABEL[k])}</span>;
  };

  const backBtn = (
    <button
      type="button"
      className="payout-history-back"
      onClick={() => navigate(-1)}
      aria-label={t("payoutHistoryPage.back")}
    >
      <FaArrowLeft size={16} />
    </button>
  );

  if (loading) {
    // The back arrow must exist from the first frame — the loading state
    // previously rendered only the Loader, so the arrow was missing for the
    // first seconds after navigating in (founder report).
    return (
      /* The page owns its scroll — see .ph-scroll. */
      <div className="ph-scroll">
      <div className="payout-history-page">
        {backBtn}
        <div className="payout-history-loader-center">
          <Loader />
        </div>
      </div>
      </div>
    );
  }

  return (
    <>
      {/* The page owns its scroll — see .ph-scroll. */}
      <div className="ph-scroll">
    <div className="payout-history-page">
      {backBtn}
      <header className="payout-history-header">
        <div className="payout-history-avatar-wrap">
          {profile?.avatarUrl && !avatarBroken ? (
            <img
              className="payout-history-avatar"
              src={absMediaUrl(profile.avatarUrl) ?? undefined}
              alt=""
              // A photo URL that 404s (file gone, stale path) painted WebKit's
              // broken-image glyph inside the ring on the sim (sim sweep).
              // Fall back to the same initial the no-photo state shows.
              onError={() => setAvatarBroken(true)}
            />
          ) : (
            <div className="payout-history-avatar payout-history-avatar--placeholder" aria-hidden>
              {displayName.slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <h1 className="payout-history-name">{displayName}</h1>
        <h2 className="payout-history-title">{t("payoutHistoryPage.payoutHistory")}</h2>
      </header>

      {error && <p className="payout-history-error">{error}</p>}

      {/* One line per payout (founder pick B, build 44): the status stands
          alone on its line; where the money went sits under the date. */}
      <div className="payout-history-card payout-history-table-card">
        <div className="payout-history-table-scroll">
          <table className="payout-history-table">
            <thead>
              <tr>
                <th className="col-date">{t("payoutHistoryPage.date")}</th>
                <th className="col-net">{t("payoutHistoryPage.net")}</th>
                <th className="col-status">{t("payoutHistoryPage.status")}</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr>
                  <td colSpan={3} className="payout-history-empty">
                    {t("payoutHistoryPage.noPayoutsYet")}
                  </td>
                </tr>
              ) : (
                payouts.map((p, i) => {
                  const dest = payoutDestinationView(p);
                  const year = yearOf(p.createdAt);
                  const pastYear = year !== new Date().getFullYear();
                  // Newest first: a year row heads each earlier year's payouts.
                  const yearRow = pastYear && (i === 0 || yearOf(payouts[i - 1].createdAt) !== year);
                  return (
                    <React.Fragment key={p._id}>
                    {yearRow && (
                      <tr className="payout-year-row">
                        <td colSpan={3}>{year}</td>
                      </tr>
                    )}
                    <tr
                      className="payout-history-row"
                      onClick={() =>
                        isMoving(p)
                          ? setTracking(p)
                          : setOpenPayout((cur) =>
                              cur?.batchId === p.batchId
                                ? null
                                : { batchId: p.batchId, label: fmtDate(p.createdAt, pastYear) }
                            )
                      }
                    >
                      <td className="col-date">
                        <div className="payout-date-day">{fmtDate(p.createdAt)}</div>
                        {dest && (
                          <div className="payout-dest" data-kind={dest.kind} title={dest.title}>
                            <DestinationIcon kind={dest.kind} />
                            <span className="payout-dest-text">{dest.text}</span>
                          </div>
                        )}
                      </td>
                      <td className="col-net">
                        <span className="payout-history-money">{money(p)}</span>
                      </td>
                      <td className="col-status">
                        <div className="payout-status-cell">
                          {status(p)}
                          <Chevron />
                        </div>
                      </td>
                    </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      </div>
      </div>

      {/* Overlays render OUTSIDE .ph-scroll: a composited scroller can clip
          its position:fixed descendants on iOS WebKit (STACKING CONTRACT v2). */}
      {openPayout && (
        <PayoutDetailsModal
          slug={user.slug}
          batchId={openPayout.batchId}
          payoutLabel={openPayout.label}
          referralBonus={referralBonusOf(payouts.find((p) => p.batchId === openPayout.batchId)?.totalsByCurrency)}
          onClose={() => setOpenPayout(null)}
        />
      )}

      {tracking && (
        <PayoutTrackerSheet
          payout={tracking}
          onClose={() => setTracking(null)}
          onSeeRequests={() => {
            setOpenPayout({ batchId: tracking.batchId, label: fmtDate(tracking.createdAt, yearOf(tracking.createdAt) !== new Date().getFullYear()) });
            setTracking(null);
          }}
        />
      )}

      {chargebackUploadFor && (
        <ChargebackUploadModal
          batchId={chargebackUploadFor.batchId}
          pendingCount={chargebackUploadFor.pendingCount}
          onClose={() => setChargebackUploadFor(null)}
          onUploaded={() => {
            // Optimistically clear this row's "needs evidence" count so the
            // Action Needed badge goes at once. The server is the source of
            // truth on the next fetch.
            setPayouts((prev) =>
              prev.map((row) =>
                row.batchId === chargebackUploadFor.batchId
                  ? { ...row, disputedOrdersNeedingTrackHistory: 0 }
                  : row
              )
            );
            setChargebackUploadFor(null);
          }}
        />
      )}
    </>
  );
};

export default PayoutHistoryPage;
